import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Group, Select, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';

import { assignVisit, listOrganizationMembers, listUsers, unassignVisit } from '../../api/resources';
import { VISIT_ASSIGNABLE_STATUSES } from '../../types/domain';
import { fieldOfficerUsers } from '../../lib/fieldOfficers';
import { userLabel } from '../../lib/labels';
import { DetailSection } from '../detail/DetailSection';

export function AssignVisitSection({
    visitId,
    visitStatus,
    currentFieldOfficerId,
    dispatchOrganizationId,
}: {
    visitId: string;
    visitStatus?: string;
    currentFieldOfficerId?: string;
    /** Dispatch org = the org whose field officers can take this visit
     *  (`case.executionOrganizationId ?? case.organizationId`). */
    dispatchOrganizationId?: string;
}) {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const [assignedAgentId, setAssignedAgentId] = useState<string | null>(
        currentFieldOfficerId ?? null,
    );
    const [reason, setReason] = useState('');

    useEffect(() => {
        setAssignedAgentId(currentFieldOfficerId ?? null);
    }, [currentFieldOfficerId]);

    // Assignable officers are field officers of the dispatch org. Fall back to
    // the legacy user list only when the dispatch org can't be resolved.
    const membersQ = useQuery({
        queryKey: ['organization-members', 'dispatch', dispatchOrganizationId],
        queryFn: () =>
            listOrganizationMembers({
                page: 1,
                limit: 500,
                organizationId: dispatchOrganizationId!,
            }),
        enabled: Boolean(dispatchOrganizationId),
    });
    const usersQ = useQuery({
        queryKey: ['users-field-officers'],
        queryFn: () => listUsers({ page: 1, limit: 500 }),
        enabled: !dispatchOrganizationId,
    });

    const assignMutation = useMutation({
        mutationFn: () =>
            assignVisit(visitId, {
                assignedAgentId: assignedAgentId!,
                ...(reason.trim() ? { reason: reason.trim() } : {}),
            }),
        onSuccess: () => {
            notifications.show({ color: 'green', message: t('features.visits.assigned') });
            setReason('');
            void queryClient.invalidateQueries({ queryKey: ['visit', visitId] });
            void queryClient.invalidateQueries({ queryKey: ['visits'] });
        },
        onError: (e: Error) => notifications.show({ color: 'red', message: e.message }),
    });

    const unassignMutation = useMutation({
        mutationFn: () => unassignVisit(visitId),
        onSuccess: () => {
            notifications.show({ color: 'green', message: t('features.visits.unassigned') });
            setAssignedAgentId(null);
            void queryClient.invalidateQueries({ queryKey: ['visit', visitId] });
            void queryClient.invalidateQueries({ queryKey: ['visits'] });
        },
        onError: () =>
            notifications.show({ color: 'red', message: t('features.visits.couldNotUnassign') }),
    });

    const fieldOfficers = dispatchOrganizationId
        ? fieldOfficerUsers(membersQ.data?.data ?? [])
        : (usersQ.data?.data ?? []).filter((u) => !u.isPlatformAdmin);
    const canUnassign =
        Boolean(currentFieldOfficerId) && visitStatus?.toLowerCase() === 'assigned';

    // Assignment only applies before the visit is submitted for review.
    if (visitStatus && !VISIT_ASSIGNABLE_STATUSES.has(visitStatus)) {
        return null;
    }

    return (
        <DetailSection
            title={t('detail.assignVisit')}
            description={t('detail.assignVisitHint')}
        >
            <Group align="flex-end" grow>
                <Select
                    label={t('fields.fieldOfficer')}
                    placeholder={t('features.visits.selectFieldOfficer')}
                    required
                    searchable
                    data={fieldOfficers.map((u) => ({
                        value: u.id,
                        label: userLabel(u),
                    }))}
                    value={assignedAgentId}
                    onChange={setAssignedAgentId}
                />
                <TextInput
                    label={t('fields.reason')}
                    placeholder={t('features.visits.assignReasonPlaceholder')}
                    value={reason}
                    onChange={(e) => setReason(e.currentTarget.value)}
                />
                <Button
                    disabled={!assignedAgentId || assignedAgentId === currentFieldOfficerId}
                    loading={assignMutation.isPending}
                    onClick={() => assignMutation.mutate()}
                >
                    {t('common.actions.assign')}
                </Button>
                {canUnassign && (
                    <Button
                        variant="subtle"
                        color="red"
                        loading={unassignMutation.isPending}
                        onClick={() => unassignMutation.mutate()}
                    >
                        {t('common.actions.unassign')}
                    </Button>
                )}
            </Group>
        </DetailSection>
    );
}
